/**
 * Inline viewer for invoice attachments.
 *
 * An approver should not have to download a file to read it — the download is a
 * context switch, it leaves commercial documents scattered on laptops, and on a
 * tablet it often just fails. So the bytes are fetched through the same
 * authenticated client the rest of the module uses and rendered in place.
 *
 * What it will NOT do is pretend. Word and PowerPoint cannot be rendered in the
 * browser without shipping a converter, so those say so plainly and offer the
 * download instead of showing an empty frame.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Download, FileSpreadsheet, FileText, Image as ImageIcon, Loader2, Maximize2,
  Minus, Plus, RotateCcw, X,
} from 'lucide-react';
import api from '@/lib/api';
import { int } from './iptTheme';

const kindOf = (doc) => {
  const n = (doc?.filename || '').toLowerCase();
  const ct = (doc?.content_type || '').toLowerCase();
  if (ct.includes('pdf') || n.endsWith('.pdf')) return 'pdf';
  if (ct.startsWith('image/') || /\.(png|jpe?g|webp|gif|bmp|tiff?|heic)$/.test(n)) return 'image';
  if (n.endsWith('.csv') || ct === 'text/csv') return 'csv';
  if (/\.(xlsx|xlsm|xls)$/.test(n) || ct.includes('spreadsheet') || ct.includes('excel')) return 'sheet';
  if (ct.startsWith('text/') || /\.(txt|json|xml|md|log|htm|html)$/.test(n)) return 'text';
  return 'other';
};

const LABEL = {
  pdf: 'PDF', image: 'Image', csv: 'Table', sheet: 'Spreadsheet', text: 'Text',
  other: 'Not previewable',
};

export const IptDocViewer = ({ doc, onClose, T }) => {
  const kind = useMemo(() => kindOf(doc), [doc]);
  const [state, setState] = useState({ loading: true, error: null });
  const [url, setUrl] = useState(null);
  const [text, setText] = useState('');
  const [sheets, setSheets] = useState([]);
  const [sheetIdx, setSheetIdx] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  useEffect(() => {
    let objUrl = null;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/ipt/documents/${doc.document_id}/download`,
          { responseType: 'blob' });
        if (cancelled) return;
        if (kind === 'pdf' || kind === 'image') {
          objUrl = URL.createObjectURL(r.data);
          setUrl(objUrl);
        } else if (kind === 'csv' || kind === 'text') {
          setText(await r.data.text());
        } else if (kind === 'sheet') {
          const XLSX = await import('xlsx');
          const wb = XLSX.read(await r.data.arrayBuffer(), { type: 'array' });
          setSheets(wb.SheetNames.map((name) => ({
            name,
            rows: XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, blankrows: false,
              defval: '' }),
          })));
        }
        setState({ loading: false, error: null });
      } catch (e) {
        if (!cancelled) {
          setState({ loading: false, error: 'This file could not be opened for preview.' });
        }
      }
    })();
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [doc.document_id, kind]);

  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);

  const download = useCallback(async () => {
    const r = await api.get(`/ipt/documents/${doc.document_id}/download`,
      { responseType: 'blob' });
    const u = URL.createObjectURL(r.data);
    const a = window.document.createElement('a');
    a.href = u; a.download = doc.filename || doc.document_id;
    window.document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(u), 4000);
  }, [doc]);

  const Icon = { sheet: FileSpreadsheet, csv: FileSpreadsheet, image: ImageIcon }[kind]
    || FileText;

  const csvRows = useMemo(() => (kind === 'csv'
    ? text.split(/\r?\n/).filter((l) => l.trim()).map((l) => l.split(','))
    : []), [kind, text]);
  const rows = kind === 'csv' ? csvRows : (sheets[sheetIdx]?.rows || []);

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-[90]"
        style={{ background: 'rgba(8,12,22,0.62)', backdropFilter: 'blur(2px)' }} />
      <div data-testid="ipt-doc-viewer"
        className="fixed z-[91] flex flex-col rounded-lg overflow-hidden shadow-2xl"
        style={{ inset: '3vh 4vw', background: T.page, border: `1px solid ${T.border}` }}>
        <header className="px-4 py-2.5 flex items-center gap-3 shrink-0"
          style={{ background: T.surface, borderBottom: `1px solid ${T.border}` }}>
          <Icon className="w-4 h-4 shrink-0" style={{ color: T.primary }} />
          <div className="min-w-0">
            <h2 className="text-[13px] font-semibold truncate" style={{ color: T.ink }}
              data-testid="ipt-doc-viewer-title">{doc.filename}</h2>
            <p className="text-[9.5px]" style={{ color: T.muted }}>
              {LABEL[kind]} · {doc.size ? `${int(doc.size / 1024)} KB` : '—'}
              {doc.category ? ` · ${doc.category}` : ''}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            {kind === 'image' && (
              <>
                <IconBtn T={T} testId="ipt-doc-viewer-zoom-out"
                  onClick={() => setZoom((z) => Math.max(0.2, z - 0.25))}><Minus className="w-3.5 h-3.5" /></IconBtn>
                <span className="text-[10px] tabular-nums w-10 text-center"
                  style={{ color: T.muted }}>{Math.round(zoom * 100)}%</span>
                <IconBtn T={T} testId="ipt-doc-viewer-zoom-in"
                  onClick={() => setZoom((z) => Math.min(6, z + 0.25))}><Plus className="w-3.5 h-3.5" /></IconBtn>
                <IconBtn T={T} testId="ipt-doc-viewer-reset"
                  onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}><RotateCcw className="w-3.5 h-3.5" /></IconBtn>
              </>
            )}
            {url && (
              <IconBtn T={T} testId="ipt-doc-viewer-newtab"
                onClick={() => window.open(url, '_blank', 'noopener')}>
                <Maximize2 className="w-3.5 h-3.5" />
              </IconBtn>
            )}
            <IconBtn T={T} testId="ipt-doc-viewer-download" onClick={download}>
              <Download className="w-3.5 h-3.5" />
            </IconBtn>
            <IconBtn T={T} testId="ipt-doc-viewer-close" onClick={onClose}>
              <X className="w-4 h-4" />
            </IconBtn>
          </div>
        </header>

        <div className="flex-1 min-h-0 overflow-auto" style={{ background: T.surfaceAlt }}>
          {state.loading && (
            <div className="h-full grid place-items-center" data-testid="ipt-doc-viewer-loading">
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: T.primary }} />
            </div>
          )}
          {!state.loading && state.error && (
            <Fallback T={T} onDownload={download} testId="ipt-doc-viewer-error"
              title="Preview failed" body={state.error} />
          )}
          {!state.loading && !state.error && kind === 'pdf' && (
            <iframe title={doc.filename} src={url} data-testid="ipt-doc-viewer-pdf"
              className="w-full h-full" style={{ border: 0, background: '#fff' }} />
          )}
          {!state.loading && !state.error && kind === 'image' && (
            <div className="w-full h-full overflow-hidden grid place-items-center cursor-grab"
              data-testid="ipt-doc-viewer-image-wrap"
              onWheel={(e) => { e.preventDefault();
                setZoom((z) => Math.min(6, Math.max(0.2, z - e.deltaY * 0.0015))); }}
              onMouseDown={(e) => { drag.current = { x: e.clientX - pan.x, y: e.clientY - pan.y }; }}
              onMouseMove={(e) => { if (drag.current) {
                setPan({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y }); } }}
              onMouseUp={() => { drag.current = null; }}
              onMouseLeave={() => { drag.current = null; }}>
              <img src={url} alt={doc.filename} data-testid="ipt-doc-viewer-image"
                draggable={false}
                style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transition: drag.current ? 'none' : 'transform 120ms ease-out',
                  maxWidth: 'none' }} />
            </div>
          )}
          {!state.loading && !state.error && (kind === 'csv' || kind === 'sheet') && (
            <div className="p-3">
              {kind === 'sheet' && sheets.length > 1 && (
                <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                  {sheets.map((s, i) => (
                    <button key={s.name} type="button" onClick={() => setSheetIdx(i)}
                      data-testid={`ipt-doc-viewer-sheet-${i}`}
                      className="px-2 py-1 rounded text-[10px] font-semibold"
                      style={i === sheetIdx
                        ? { background: T.primary, color: '#fff' }
                        : { background: T.surface, color: T.body, border: `1px solid ${T.border}` }}>
                      {s.name}
                    </button>
                  ))}
                </div>
              )}
              <div className="rounded-md overflow-auto"
                style={{ background: T.surface, border: `1px solid ${T.border}` }}>
                <table className="w-full text-[11px]" data-testid="ipt-doc-viewer-table">
                  <tbody>
                    {rows.slice(0, 500).map((r, ri) => (
                      <tr key={ri} style={{ borderTop: ri ? `1px solid ${T.border}` : 'none',
                        background: ri === 0 ? T.surfaceAlt : 'transparent' }}>
                        {(Array.isArray(r) ? r : [r]).map((c, ci) => (
                          <td key={ci} className="py-1.5 px-2 align-top whitespace-pre-wrap"
                            style={{ color: ri === 0 ? T.ink : T.body,
                              fontWeight: ri === 0 ? 600 : 400 }}>
                            {String(c ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length > 500 && (
                <p className="text-[10px] mt-2" style={{ color: T.muted }}>
                  Showing the first 500 of {int(rows.length)} rows. Download the file for the rest.
                </p>
              )}
            </div>
          )}
          {!state.loading && !state.error && kind === 'text' && (
            <pre className="p-4 text-[11px] whitespace-pre-wrap" data-testid="ipt-doc-viewer-text"
              style={{ color: T.body, fontFamily: 'ui-monospace, monospace' }}>{text}</pre>
          )}
          {!state.loading && !state.error && kind === 'other' && (
            <Fallback T={T} onDownload={download} testId="ipt-doc-viewer-unsupported"
              title="This format cannot be shown in the browser"
              body={`${doc.filename} is a ${doc.content_type || 'binary'} file. Rendering Word or
                     PowerPoint faithfully needs a server-side converter, and showing a rough
                     approximation of a commercial document would be worse than showing nothing.
                     Download it to read it.`} />
          )}
        </div>
      </div>
    </>
  );
};

const IconBtn = ({ children, onClick, T, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className="p-1.5 rounded-md hover:opacity-75"
    style={{ background: T.surfaceAlt, border: `1px solid ${T.border}`, color: T.body }}>
    {children}
  </button>
);

const Fallback = ({ title, body, onDownload, T, testId }) => (
  <div className="h-full grid place-items-center p-8" data-testid={testId}>
    <div className="max-w-md text-center space-y-3">
      <FileText className="w-7 h-7 mx-auto" style={{ color: T.muted }} />
      <h3 className="text-[13px] font-semibold" style={{ color: T.ink }}>{title}</h3>
      <p className="text-[11.5px] leading-relaxed" style={{ color: T.body }}>{body}</p>
      <button type="button" onClick={onDownload} data-testid="ipt-doc-viewer-fallback-download"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold"
        style={{ background: T.primary, color: '#fff' }}>
        <Download className="w-3 h-3" /> Download
      </button>
    </div>
  </div>
);

export default IptDocViewer;
